import { useEffect, useRef } from 'react';
import { useTypedSearchParams } from 'react-router-typesafe-routes';
import { type Components, type StateSnapshot, Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import EmptyLayout from '@/components/layouts/empty-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import FilterSessionDropdown from '@/components/sessions/session-list/filter-session-dropdown';
import { SessionListItem } from '@/components/sessions/session-list/session-list-item';
import { useFilteredSessions } from '@/hooks/sessions/use-conversation-sessions';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { ConversationSessionsTypeUnion } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { clearLastPosition, useConversationSessionStore } from '@/stores/conversation-session';
import { generateSessionKey } from '@/utils/session';

const components: Components<ConversationSessionsTypeUnion> = {
  Item: ({ children, ...props }) => (
    <>
      <div
        className='group mt-1 desktop:px-5'
        {...props}>
        {children}
      </div>
      <div className='border-b mt-1 border-base-100 mx-4' />
    </>
  ),
  EmptyPlaceholder: () => <EmptyLayout label='conversation.list.empty' />,
  Footer: () => <div className='mode-embedded:pb-safe' />,
};

export default function SessionList() {
  const listRef = useRef<VirtuosoHandle>(null);
  const [searchParams, setSearchParams] = useTypedSearchParams(ROUTES.MESSAGES);
  const { availableSessions, isLoading, isPlaceholderData, filter, setFilter } = useFilteredSessions(
    searchParams.filter,
  );
  const lastPosition = useConversationSessionStore.getState().lastPositions[ROUTES.MESSAGES.$path()]
    ?.default as StateSnapshot;
  const isDesktop = useIsDesktop();

  const handleKeepListPosition = () => {
    listRef.current?.getState((state) => {
      useConversationSessionStore.getState().setLastPosition(ROUTES.MESSAGES.$path(), { ...state, anchorId: 0 });
    });
  };

  useEffect(() => {
    clearLastPosition(undefined, { keepMainListPosition: true });
  }, []);

  return (
    <LoadingLayout promise={isPlaceholderData || isLoading}>
      {() => (
        <>
          <div className='flex justify-end my-1 mx-5'>
            <FilterSessionDropdown
              filter={filter}
              setFilter={setFilter}
              onChange={(f) => {
                if (listRef.current) {
                  setSearchParams((qs) => ({ ...qs, filter: f }), { replace: true });
                  listRef.current.scrollToIndex({ index: 0, behavior: 'smooth' });
                }
              }}
            />
          </div>
          <Virtuoso
            ref={listRef}
            className='flex flex-1 flex-col items-stretch desktop:!overflow-y-scroll'
            itemContent={(_, session) => (
              <SessionListItem
                key={generateSessionKey(session.SessionType, session.ID)}
                session={session}
                onLinkClick={isDesktop ? undefined : handleKeepListPosition}
              />
            )}
            data={availableSessions}
            defaultItemHeight={60}
            overscan={6}
            components={components}
            restoreStateFrom={lastPosition}
          />
        </>
      )}
    </LoadingLayout>
  );
}
